import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { UserAvatar } from '@/components/common/UserAvatar';
import { Button, Icon, IconButton, TextInput } from '@/components/ds';
import {
  useAdminConversations,
  useConversationReadState,
  useConversationViews,
} from '@/hooks/admin/useAdminConversations';
import { useClientGroupChat } from '@/hooks/useClientGroupChat';
import { ChatMessageStatus } from '@/models/ChatMessage';
import { GroupChatMessage } from '@/models/GroupChatMessage';
import { AdminConversation, recordConversationView } from '@/services/api/adminConversations';

// Messages "god mode": the brokers' (Admin/SuperAdmin) oversight of every advisor–client thread on the platform.
// Three panes: who to look at (filters), the threads, and the thread itself. Opening a thread observes it silently
// (no read receipt for the participants) and records the view in the oversight trail; stepping in is one deliberate
// move, "Reply as Support", which posts as Support.

const font = "'Manrope', sans-serif";
const C = {
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  faint: 'var(--tx-faint)',
  line: 'var(--tx-line)',
  critical: '#DB5282',
  urgent: '#FF9A4D',
  low: '#5AE9FF',
  lime: '#C0FF02',
};
const ROLE_COLOR: Record<string, string> = { advisor: C.urgent, support: C.lime, member: C.low };
const ROLE_LABEL: Record<string, string> = { advisor: 'Advisor', support: 'Support', member: 'Client' };

const sheet: React.CSSProperties = {
  background: 'var(--tx-glass-panel)',
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  borderRadius: 24,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  fontFamily: font,
};

const tick = (color: string, h = 12) => (
  <span style={{ width: 2, height: h, borderRadius: 1, background: color, flexShrink: 0 }} />
);

const ago = (ts?: number) => {
  if (!ts) return '';
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const clock = (ts: number) =>
  new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

type Filter = 'all' | 'awaiting' | 'active' | 'quiet' | 'archived';
const DAY = 24 * 60 * 60 * 1000;

export const MessagesOversight = () => {
  const { conversations, degraded, isLoading } = useAdminConversations();
  const [filter, setFilter] = useState<Filter>('all');
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const active = conversations.filter((c) => c.status === 'active');
    return {
      all: active.length,
      awaiting: active.filter((c) => c.awaitingReply).length,
      active: active.filter((c) => c.lastActivityAt && Date.now() - c.lastActivityAt < DAY).length,
      quiet: active.filter((c) => !c.messageCount).length,
      archived: conversations.length - active.length,
    };
  }, [conversations]);

  const advisors = useMemo(() => {
    const m = new Map<string, { id: string; name: string; photoUrl?: string; n: number; waiting: number }>();
    conversations.forEach((c) => {
      const a = m.get(c.advisor.userId) ?? { id: c.advisor.userId, name: c.advisor.name, photoUrl: c.advisor.photoUrl, n: 0, waiting: 0 };
      a.n += 1;
      if (c.awaitingReply) a.waiting += 1;
      m.set(c.advisor.userId, a);
    });
    return [...m.values()].sort((x, y) => y.waiting - x.waiting || y.n - x.n);
  }, [conversations]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations
      .filter((c) => (filter === 'archived' ? c.status === 'archived' : c.status === 'active'))
      .filter((c) => filter !== 'awaiting' || c.awaitingReply)
      .filter((c) => filter !== 'active' || (c.lastActivityAt && Date.now() - c.lastActivityAt < DAY))
      .filter((c) => filter !== 'quiet' || !c.messageCount)
      .filter((c) => !advisorId || c.advisor.userId === advisorId || c.coAdvisors.some((a) => a.userId === advisorId))
      .filter(
        (c) =>
          !q ||
          c.holdingName.toLowerCase().includes(q) ||
          c.advisor.name.toLowerCase().includes(q) ||
          c.participants.some((p) => p.name.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q)),
      )
      .sort((a, b) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0) || a.holdingName.localeCompare(b.holdingName));
  }, [conversations, filter, advisorId, search]);

  const selected = conversations.find((c) => c.groupId === selectedId) ?? null;

  const filterRow = (key: Filter, label: string, n: number, color?: string) => {
    const on = filter === key;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={on}
        onClick={() => setFilter(key)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 36,
          padding: '0 10px',
          border: 0,
          borderRadius: 10,
          background: on ? 'var(--tx-sel-bg)' : 'transparent',
          boxShadow: on ? 'var(--tx-sel-shadow)' : 'none',
          color: on ? C.text : C.muted,
          fontFamily: font,
          fontSize: 13,
          fontWeight: on ? 600 : 500,
          cursor: 'pointer',
          textAlign: 'left',
        }}>
        {tick(color ?? 'transparent')}
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ color: n ? C.text : C.faint, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
      </button>
    );
  };

  return (
    <div
      style={{
        height: '100vh',
        boxSizing: 'border-box',
        padding: '20px 0',
        display: 'grid',
        gridTemplateColumns: '240px minmax(300px, 400px) minmax(0, 1fr)',
        gap: 20,
        fontFamily: font,
      }}>
      {/* ─── Who to look at ─────────────────────────────────── */}
      <aside style={{ ...sheet, padding: 14, gap: 14 }}>
        <TextInput icon="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client, advisor, email…" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filterRow('all', 'All threads', counts.all)}
          {filterRow('awaiting', 'Awaiting reply', counts.awaiting, C.critical)}
          {filterRow('active', 'Active today', counts.active, C.lime)}
          {filterRow('quiet', 'No messages yet', counts.quiet)}
          {filterRow('archived', 'Archived', counts.archived)}
        </div>
        <div style={{ height: 1, background: C.line }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: C.faint, padding: '0 10px' }}>Advisors</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', scrollbarWidth: 'none', minHeight: 0 }}>
          {advisors.map((a) => {
            const on = advisorId === a.id;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={on}
                onClick={() => setAdvisorId(on ? null : a.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 40,
                  padding: '0 10px',
                  border: 0,
                  borderRadius: 10,
                  background: on ? 'var(--tx-sel-bg)' : 'transparent',
                  boxShadow: on ? 'var(--tx-sel-shadow)' : 'none',
                  fontFamily: font,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}>
                <UserAvatar name={a.name} photoUrl={a.photoUrl} size={24} showPresence={false} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: on ? 600 : 500,
                    color: on ? C.text : C.muted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {a.name}
                </span>
                {a.waiting > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: C.text }}>
                    {tick(C.critical, 10)}
                    {a.waiting}
                  </span>
                )}
                <span style={{ fontSize: 12, fontWeight: 600, color: C.faint, fontVariantNumeric: 'tabular-nums' }}>{a.n}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ─── The threads ───────────────────────────────────── */}
      <section style={sheet}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '18px 18px 10px' }}>
          <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text }}>Threads</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>
            {degraded ? 'Activity appears once deployed' : `${visible.length} shown`}
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', padding: '0 8px 8px' }}>
          {isLoading ? (
            <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: C.faint }}>Loading…</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: C.faint }}>No threads match</div>
          ) : (
            visible.map((c) => <ThreadRow key={c.groupId} c={c} on={c.groupId === selectedId} onSelect={() => setSelectedId(c.groupId)} />)
          )}
        </div>
      </section>

      {/* ─── The thread ────────────────────────────────────── */}
      <section style={sheet}>
        {selected ? (
          <Transcript key={selected.groupId} c={selected} degraded={degraded} />
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: C.faint }}>
            <Icon name="eye" size={22} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Pick a thread to observe it</span>
            <span style={{ fontSize: 11, fontWeight: 500 }}>Participants aren&apos;t notified; your view is logged for oversight</span>
          </div>
        )}
      </section>
    </div>
  );
};

// ─── Thread row ─────────────────────────────────────────────────────────────
function ThreadRow({ c, on, onSelect }: { c: AdminConversation; on: boolean; onSelect: () => void }) {
  const client = c.participants[0];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={on}
      style={{
        width: '100%',
        display: 'flex',
        gap: 12,
        padding: '12px 10px',
        border: 0,
        borderRadius: 14,
        background: on ? 'var(--tx-sel-bg)' : 'transparent',
        boxShadow: on ? 'var(--tx-sel-shadow)' : 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: font,
      }}
      onMouseEnter={(e) => !on && (e.currentTarget.style.background = 'var(--tx-fill)')}
      onMouseLeave={(e) => !on && (e.currentTarget.style.background = 'transparent')}>
      <UserAvatar name={client?.name ?? c.holdingName} photoUrl={client?.photoUrl} size={34} showPresence={false} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 600,
              color: C.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {c.holdingName}
          </span>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.faint, flexShrink: 0 }}>{ago(c.lastActivityAt)}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: C.muted, minWidth: 0 }}>
          {tick(C.urgent, 10)}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.advisor.name}</span>
          {c.messageCount > 0 && <span style={{ color: C.faint, flexShrink: 0 }}>· {c.messageCount}</span>}
        </span>
        {c.lastMessage ? (
          <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ color: C.text }}>{c.lastMessage.senderName.split(' ')[0]}:</span>{' '}
            {c.lastMessage.deleted ? <i>Message deleted</i> : c.lastMessage.content}
          </span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: C.faint }}>No messages yet</span>
        )}
        {c.awaitingReply && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: C.text, marginTop: 2 }}>
            {tick(C.critical, 10)}
            Waiting on a reply {c.awaitingSince ? `· ${ago(c.awaitingSince)}` : ''}
          </span>
        )}
      </span>
    </button>
  );
}

// ─── Transcript ─────────────────────────────────────────────────────────────
function Transcript({ c, degraded }: { c: AdminConversation; degraded: boolean }) {
  const { messages, sendMessage, sendDeleteMessage, isLoadingMessages, hasMoreMessages, loadMoreMessages, isFetchingMore } =
    useClientGroupChat(c.groupId);
  const readState = useConversationReadState(c.groupId, !degraded);
  const views = useConversationViews(c.groupId, !degraded);
  const record = useMutation({ mutationFn: () => recordConversationView(c.groupId), onSuccess: () => views.refetch() });
  const [replyOpen, setReplyOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // the oversight trail: opening a thread records who looked, once per opening (the backend dedupes repeats)
  useEffect(() => {
    if (!degraded) record.mutate();
  }, [c.groupId, degraded]);

  const ordered = useMemo(() => [...messages].sort((a, b) => a.createdAt - b.createdAt), [messages]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [ordered.length]);

  const lastRead = (userId?: string) => readState.data?.participants.find((p) => p.userId === userId)?.lastReadAt;
  const people = [
    { key: c.advisor.userId, name: c.advisor.name, role: 'advisor', userId: c.advisor.userId },
    ...c.coAdvisors.map((a) => ({ key: a.userId, name: a.name, role: 'advisor', userId: a.userId })),
    ...c.participants.map((p, i) => ({ key: p.userId ?? `m${i}`, name: p.name, role: 'member', userId: p.userId })),
  ];

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    if (sendMessage(text)) {
      setDraft('');
      setReplyOpen(false);
    }
  };

  return (
    <>
      {/* header: the thread, its people and when each last read it */}
      <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text }}>{c.holdingName}</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>
            {c.messageCount ? `${c.messageCount} messages` : ''}
            {c.status === 'archived' ? ' · archived' : ''}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, color: C.faint }}>
            <Icon name="eye" size={13} />
            Observing · not visible to participants
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
          {people.map((p) => {
            const read = lastRead(p.userId);
            return (
              <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: C.text }}>
                {tick(ROLE_COLOR[p.role])}
                {p.name}
                <span style={{ color: C.faint }}>{read ? `read ${ago(read)}` : ROLE_LABEL[p.role]}</span>
              </span>
            );
          })}
        </div>
        {!!views.data?.views.length && (
          <div style={{ fontSize: 11, fontWeight: 500, color: C.faint }}>
            Viewed by{' '}
            {views.data.views
              .slice(0, 3)
              .map((v) => `${v.adminName} ${ago(v.viewedAt)}`)
              .join(' · ')}
          </div>
        )}
      </div>

      {/* the conversation */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {hasMoreMessages && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button size="sm" variant="ghost" onClick={() => loadMoreMessages()} disabled={isFetchingMore}>
              {isFetchingMore ? 'Loading…' : 'Earlier messages'}
            </Button>
          </div>
        )}
        {isLoadingMessages ? (
          <div style={{ margin: 'auto', fontSize: 13, color: C.faint }}>Loading…</div>
        ) : ordered.length === 0 ? (
          <div style={{ margin: 'auto', fontSize: 13, color: C.faint }}>No messages in this thread yet</div>
        ) : (
          ordered.map((m) => (
            <MessageRow
              key={`${m.createdAt}-${m.messageId}`}
              m={m}
              confirming={confirmDelete === m.createdAt}
              onAskDelete={() => setConfirmDelete(m.createdAt)}
              onCancelDelete={() => setConfirmDelete(null)}
              onDelete={() => {
                sendDeleteMessage(m.createdAt);
                setConfirmDelete(null);
              }}
            />
          ))
        )}
        <div ref={endRef} />
      </div>

      {/* stepping in: folded away until asked for, one move to open, labelled as Support */}
      <div style={{ borderTop: `1px solid ${C.line}`, padding: replyOpen ? 14 : '10px 14px' }}>
        {!replyOpen ? (
          <button
            type="button"
            onClick={() => setReplyOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 32,
              padding: '0 10px',
              border: 0,
              borderRadius: 10,
              background: 'transparent',
              color: C.muted,
              fontFamily: font,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            {tick(C.lime)}
            Reply as Support
            <Icon name="chev" size={12} />
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
              {tick(C.lime)}
              Posting as Support · everyone in this thread will see it
              <span style={{ flex: 1 }} />
              <IconButton icon="x" label="Close reply" size="sm" variant="ghost" onClick={() => setReplyOpen(false)} />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <TextInput
                multiline
                minRows={2}
                maxRows={6}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
                }}
                placeholder="Write as Support…"
                sx={{ flex: 1 }}
              />
              <Button variant="primary" icon="send" disabled={!draft.trim()} onClick={send}>
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── One message ────────────────────────────────────────────────────────────
function MessageRow({
  m,
  confirming,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  m: GroupChatMessage;
  confirming: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}) {
  const role = m.senderRole?.toLowerCase() in ROLE_COLOR ? m.senderRole.toLowerCase() : 'member';
  const deleted = m.status === ChatMessageStatus.Deleted;
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ display: 'flex', gap: 12 }}>
      <UserAvatar name={m.senderName} size={28} showPresence={false} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ fontWeight: 600, color: C.text }}>{m.senderName}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500, color: C.muted, fontSize: 11 }}>
            {tick(ROLE_COLOR[role], 10)}
            {ROLE_LABEL[role]}
          </span>
          <span style={{ fontSize: 11, fontWeight: 500, color: C.faint }}>
            {clock(m.createdAt)}
            {m.edited ? ' · edited' : ''}
          </span>
          <span style={{ flex: 1 }} />
          {!deleted && (hover || confirming) && !confirming && (
            <IconButton icon="trash" label="Delete message" size="sm" variant="ghost" onClick={onAskDelete} />
          )}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            lineHeight: 1.55,
            color: deleted ? C.faint : C.text,
            fontStyle: deleted ? 'italic' : 'normal',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
          {deleted ? 'Message deleted' : m.content}
        </div>
        {!deleted && !!m.attachments?.length && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {m.attachments.map((a) => (
              <span
                key={a.s3Key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 26,
                  padding: '0 10px',
                  borderRadius: 8,
                  background: 'var(--tx-fill)',
                  fontSize: 11,
                  fontWeight: 500,
                  color: C.muted,
                }}>
                <Icon name="clip" size={12} />
                {a.filename}
              </span>
            ))}
          </div>
        )}
        {!!m.reactions?.length && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            {m.reactions.map((r, i) => (
              <span key={`${r.userId}-${i}`} title={r.userName} style={{ fontSize: 13 }}>
                {r.emoji}
              </span>
            ))}
          </div>
        )}
        {confirming && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, fontWeight: 500, color: C.text }}>
            {tick(C.critical)}
            Delete for everyone in this thread?
            <Button size="sm" variant="ghost" onClick={onCancelDelete}>
              Cancel
            </Button>
            <Button size="sm" variant="danger" onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessagesOversight;
